import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Text,
  Heading,
  Button,
  Container,
  Flex,
  Link,
  SimpleGrid,
  Image,
  Card,
  AspectRatio
} from '@chakra-ui/react';

import { FaExternalLinkAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { CgPexels } from 'react-icons/cg';
import { SOCIAL_MEDIA_LINKS } from './core/constants';
import { COLORS } from '../theme';
import { FONTS } from '../constants/fonts';
import { useColorModeValue } from './chakra-ui/color-mode';

import { Skeleton, SkeletonText } from './ui/skeleton';

function GallerySection({ galleryItems, allImages }) {
  const [galleryItemsWithImages, setGalleryItemsWithImages] = useState([]);

  useEffect(() => {
    const res = mapImagesToGalleryMetadata(galleryItems, allImages);
    setGalleryItemsWithImages(res);
  }, [galleryItems, allImages]);

  const cardBg = useColorModeValue(COLORS.glass.bg.light, COLORS.glass.bg.dark);
  const cardBorder = useColorModeValue(
    COLORS.glass.border.light,
    COLORS.glass.border.dark
  );
  const glassOverlay = useColorModeValue(
    COLORS.glass.overlay.light,
    COLORS.glass.overlay.dark
  );
  const cardShadow = useColorModeValue(
    COLORS.glass.shadow.light,
    COLORS.glass.shadow.dark
  );
  const descColor = useColorModeValue('gray.600', 'gray.400');

  const navigate = useNavigate();
  const handleClick = (urlPath) => {
    navigate(`/view-photos/${urlPath}`);
  };

  const isDataLoading = galleryItemsWithImages.length === 0;
  // Use a predictable number of skeletons to stabilize layout
  const itemsToDisplay = isDataLoading
    ? [1, 2, 3, 4, 5, 6]
    : galleryItemsWithImages;

  return (
    <>
      <Box>
        <Container maxW="container.xl">
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading size="2xl">Our Gallery</Heading>
              <Text fontSize="lg" color="gray.600" maxW="2xl">
                Explore our collection of stunning photographs across different
                categories. Each image tells a unique story and showcases our
                passion for capturing perfect moments.
              </Text>
            </VStack>

            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 3 }}
              spacing={8}
              w="full"
              gap="4"
            >
              {itemsToDisplay.map((item, index) =>
                isDataLoading ? (
                  <Card.Root
                    key={index}
                    borderRadius={'2xl'}
                    bg={cardBg}
                    border="1px solid"
                    borderColor={cardBorder}
                    overflow="hidden"
                  >
                    <AspectRatio ratio={16 / 10} w="full">
                      <Skeleton h="full" w="full" />
                    </AspectRatio>
                    <Box p={6}>
                      <SkeletonText noOfLines={3} gap="4" />
                    </Box>
                  </Card.Root>
                ) : (
                  <Card.Root
                    key={item.urlPath}
                    cursor="pointer"
                    _hover={{
                      transform: 'translateY(-8px)',
                      shadow: '2xl',
                      borderColor: COLORS.brand.primary
                    }}
                    transition="all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
                    onClick={() => handleClick(item.urlPath)}
                    borderRadius={'2xl'}
                    bg={cardBg}
                    backdropFilter={`blur(${COLORS.glass.blur})`}
                    border="1px solid"
                    borderColor={cardBorder}
                    shadow={cardShadow}
                    overflow="hidden"
                  >
                    <Card.Body gap="0" p={0}>
                      <AspectRatio ratio={16 / 10} w="full">
                        <Flex gap={2} w="full" h={'full'} p={2}>
                          {/* Left Big Image */}
                          <Box
                            flex="2"
                            h={'full'}
                            overflow="hidden"
                            borderRadius="xl"
                          >
                            <Image
                              src={item.image}
                              alt={item.title}
                              objectFit="cover"
                              w="full"
                              h="full"
                              transition="transform 0.5s"
                              _groupHover={{ transform: 'scale(1.05)' }}
                            />
                          </Box>

                          {item.topRightImage || item.bottomRightImage ? (
                            <Box flex="1" h={'full'}>
                              <Flex direction="column" gap={2} h="full">
                                {item.topRightImage && (
                                  <Box
                                    flex="1"
                                    overflow="hidden"
                                    borderRadius="xl"
                                  >
                                    <Image
                                      src={item.topRightImage}
                                      alt={`${item.title} detail 1`}
                                      objectFit="cover"
                                      w="full"
                                      h="full"
                                      transition="transform 0.5s"
                                      _groupHover={{ transform: 'scale(1.05)' }}
                                    />
                                  </Box>
                                )}
                                {item.bottomRightImage && (
                                  <Box
                                    flex="1"
                                    overflow="hidden"
                                    position="relative"
                                    w="full"
                                    h="full"
                                    borderRadius="xl"
                                  >
                                    <Image
                                      src={item.bottomRightImage}
                                      alt={`${item.title} detail 2`}
                                      objectFit="cover"
                                      w="full"
                                      h="full"
                                      transition="transform 0.5s"
                                      _groupHover={{ transform: 'scale(1.05)' }}
                                    />

                                    <Box
                                      position="absolute"
                                      top={0}
                                      left={0}
                                      w="full"
                                      h="full"
                                      bg={glassOverlay}
                                      backdropFilter={`blur(${parseInt(COLORS.glass.blur) / 4 + 'px'})`}
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      transition="background 0.3s"
                                      _hover={{ bg: 'rgba(244, 136, 91, 0.4)' }}
                                      borderRadius="xl"
                                    >
                                      <Text
                                        fontFamily={FONTS.heading}
                                        fontSize="lg"
                                        fontWeight="bold"
                                        color="white"
                                        textShadow="0 2px 4px rgba(0,0,0,0.3)"
                                      >
                                        + More
                                      </Text>
                                    </Box>
                                  </Box>
                                )}
                              </Flex>
                            </Box>
                          ) : null}
                        </Flex>
                      </AspectRatio>
                      <Box p={6}>
                        <Card.Title
                          mb="2"
                          fontSize="2xl"
                          fontFamily={FONTS.heading}
                          _groupHover={{ color: COLORS.brand.primary }}
                          transition="color 0.3s"
                        >
                          {item.title}
                        </Card.Title>
                        <Card.Description
                          fontSize="md"
                          color={descColor}
                          lineHeight="tall"
                        >
                          {item.description}
                        </Card.Description>
                      </Box>
                    </Card.Body>
                  </Card.Root>
                )
              )}
            </SimpleGrid>

            <Button
              mt={4}
              colorScheme="red"
              variant="outline"
              size="lg"
              as={Link}
              href={SOCIAL_MEDIA_LINKS.pexels}
              target="_blank"
              rel="noopener noreferrer"
            >
              <CgPexels /> View our Pexels Collection{' '}
              <FaExternalLinkAlt fontSize="sm" />
            </Button>
          </VStack>
        </Container>
      </Box>
    </>
  );
}

function mapImagesToGalleryMetadata(galleryItems, allImages) {
  return galleryItems.map((item) => {
    const images = allImages[item.urlPath] || [];

    return {
      ...item,
      image: images[0] || '',
      topRightImage: images[1] || '',
      bottomRightImage: images[2] || ''
    };
  });
}

export default GallerySection;
